import { prop } from 'ramda'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import store, { useAppSelector } from 'app/store'
import useListAction from 'core/hooks/useListAction'
import useUpdateAction from 'core/hooks/useUpdateAction'
import useUrlFilterParams from 'core/hooks/useUrlFilterParams'
import { type SessionState, sessionActions, sessionStoreKey } from 'core/session/sessionReducers'
import DataKeys from 'k8s/DataKeys'
import { getCurrentUser, hasPermissions } from 'openstack/helpers'
import {
  listAllStatewiseVirtualMachinesLite,
  listAllVirtualMachines,
  listAllVirtualMachinesNextPage,
  listSelfStatewiseVirtualMachinesLite,
  listSelfVirtualMachines,
  listSelfVirtualMachinesNextPage,
  listStatewiseVirtualMachinesLite,
  listVirtualMachines,
  listVirtualMachinesNextPage,
  pollAllVirtualMachines,
  pollSelfVirtualMachines,
  pollVirtualMachines,
  searchAllVirtualMachines,
  searchSelfVirtualMachines,
  searchVirtualMachines,
} from './actions'
import type { StatewiseVirtualMachineCount } from './newModel'
import {
  allStatewiseVirtualMachinesLiteSelector,
  allVirtualMachinesSelector,
  selfStatewiseVirtualMachinesLiteSelector,
  selfVirtualMachinesSelector,
  statewiseVirtualMachinesLiteSelector,
  virtualMachinesSelector,
} from './selectors'
import type { VMFilterParams } from './useVMFilterConfigs'
import { VMStates } from './VMStatePicklist'

interface UseVirtualMachinesPaginationConfig {
  // Optional host parameter - when provided, it will be included in all API calls
  host?: string
  // Page size from session or default
  pageSize: number
  // User context flags
  allTenants: boolean
  allOwners: boolean
  // Session key for pagination (different for VM list vs Host VM list)
  sessionKey: 'vmList' | 'hostList'
  sessionSearchParams: string
  sessionSortKey: string
  sessionSortDir: string
  sessionPageSize: string
  sessionVMCachedParams: string
  sessionAllVMCachedParams: string
  sessionSelfVMCachedParams: string
  defaultSearchParams: VMFilterParams
}

/**
 * Custom hook that encapsulates all common VM list logic
 * Used by both VirtualMachinesListPage and HostVmsListPage
 *
 * The key difference: HostVmsListPage passes a `host` parameter
 * which gets included in all API calls
 *
 * @responsibility
 * - Handles pagination for VM list
 * - Manages search and sorting
 * - Manages session state
 * - Manages polling
 * - Manages statewise VM count (global list only, see useVirtualMachinesPagination.spec.md)
 *
 * @param config - Configuration object
 * @returns {Object} - Object containing pagination state and actions
 */
export function useVirtualMachinesPagination(config: UseVirtualMachinesPaginationConfig) {
  const {
    host,
    pageSize: initialPageSize,
    allTenants,
    allOwners,
    sessionKey,
    sessionSearchParams,
    sessionSortKey,
    sessionSortDir,
    sessionPageSize,
    sessionVMCachedParams,
    sessionAllVMCachedParams,
    sessionSelfVMCachedParams,
    defaultSearchParams,
  } = config

  const dispatch = useDispatch()
  const session = useSelector(prop<string, SessionState>(sessionStoreKey))
  const { role: userRole } = getCurrentUser()
  const hasCrossTenantPermissions =
    userRole === 'admin' ||
    (hasPermissions(['nova', 'os_compute_api.servers.index.get_all_tenants:GET:/servers']) &&
      userRole !== 'member')

  // Read URL query params to seed initial filter state (e.g., ?vm_state=ACTIVE from dashboard)
  const vmStateValues = useMemo(() => VMStates.map((s) => s.value), [])
  const urlFilters = useUrlFilterParams([{ key: 'vm_state', allowedValues: vmStateValues }])

  // Pagination state
  const [currentPage, setCurrentPage] = useState(0)
  const [pageMarkers, setPageMarkers] = useState<(string | null)[]>([null])
  const [isPaginationLoading, setIsPaginationLoading] = useState(false)
  const [pageSize, setPageSize] = useState(initialPageSize)
  const [sortKey, setSortKey] = useState<string>(session?.[sessionSortKey] || 'created_at')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>(session?.[sessionSortDir] || 'desc')

  // Search state - conditionally include host, URL filters override session
  const cachedSearchParams = session?.[sessionSearchParams] ?? null
  const hasUrlFilters = Object.keys(urlFilters).length > 0
  const [searchParams, setSearchParams] = useState<Record<string, string> | null>(() => {
    const base = host ? { ...(cachedSearchParams || {}), host } : cachedSearchParams
    if (!hasUrlFilters) return base
    // URL filters take full precedence — replace session filters entirely
    return { ...(host ? { host } : {}), ...urlFilters }
  })
  const [isSearchActive, setIsSearchActive] = useState(!!host || hasUrlFilters)

  // Refs for lifecycle management
  const isInitialMount = useRef(true)
  const hasInitialDataLoaded = useRef(false)

  // Selection management — Grid writes clearSelectedRows into this ref;
  // handleSearch/handleClearSearch/handleSort call it automatically so
  // page components don't need to wrap those callbacks.
  const clearSelectionRef = useRef<(() => void) | null>(null)
  const clearSelection = useCallback(() => {
    clearSelectionRef.current?.()
  }, [])

  // Build params helper - always include host if provided
  const buildParams = useCallback(
    (additionalParams: any = {}) => {
      const baseParams = {
        limit: pageSize,
        sortKey,
        sortDir,
        ...additionalParams,
      }

      // If host is provided, merge it into searchParams
      if (host) {
        return {
          ...baseParams,
          searchParams: { ...(baseParams.searchParams || {}), host },
        }
      }

      return baseParams
    },
    [host, pageSize, sortKey, sortDir],
  )

  // ========== List Actions (Regular) ==========
  const {
    message,
    loading,
    reload,
    refreshing: vmRefreshing,
  } = useListAction(listVirtualMachines, {
    loadOnDemand: true,
    params: buildParams({ ...(searchParams && { searchParams }) }),
  })
  const data = useAppSelector(virtualMachinesSelector)

  // ========== List Actions (All Tenants) ==========
  const {
    message: allMessage,
    loading: allLoading,
    reload: allReload,
    refreshing: allVmRefreshing,
  } = useListAction(listAllVirtualMachines, {
    loadOnDemand: true,
    params: buildParams({ ...(searchParams && { searchParams }) }),
  })
  const allData = useAppSelector(allVirtualMachinesSelector)

  // ========== List Actions (Self/Member) ==========
  const {
    message: selfMessage,
    loading: selfLoading,
    reload: selfReload,
    refreshing: selfVmRefreshing,
  } = useListAction(listSelfVirtualMachines, {
    loadOnDemand: true,
    params: buildParams({ ...(searchParams && { searchParams }) }),
  })
  const selfData = useAppSelector(selfVirtualMachinesSelector)

  // ========== Statewise VM Count ==========
  const { reload: statewiseReload, loading: statewiseLoading } = useListAction(
    listStatewiseVirtualMachinesLite,
    {
      loadOnDemand: true,
      params: host ? { host } : {},
    },
  )
  const { reload: allStatewiseReload, loading: allStatewiseLoading } = useListAction(
    listAllStatewiseVirtualMachinesLite,
    {
      loadOnDemand: true,
      params: host ? { host } : {},
    },
  )
  const { reload: selfStatewiseReload, loading: selfStatewiseLoading } = useListAction(
    listSelfStatewiseVirtualMachinesLite,
    {
      loadOnDemand: true,
      params: host ? { host } : {},
    },
  )

  const vmStateCount = useAppSelector(statewiseVirtualMachinesLiteSelector)
  const allVmStateCount = useAppSelector(allStatewiseVirtualMachinesLiteSelector)
  const selfVmStateCount = useAppSelector(selfStatewiseVirtualMachinesLiteSelector)

  // ========== Polling ==========
  const { update: pollVms, updating: pollingVms } = useUpdateAction(pollVirtualMachines)
  const { update: pollAllVms, updating: pollingAllVms } = useUpdateAction(pollAllVirtualMachines)
  const { update: pollSelfVms, updating: pollingSelfVms } = useUpdateAction(pollSelfVirtualMachines)

  // ========== Helper Functions (Must be declared before use) ==========
  // Generic helper to select appropriate value based on user context
  const selectByContext = useCallback(
    <T>(allTenantsValue: T, selfValue: T, regularValue: T): T => {
      if (allTenants) return allTenantsValue
      if (!hasCrossTenantPermissions && !allOwners) return selfValue
      return regularValue
    },
    [allTenants, userRole, allOwners],
  )

  // Helper to check if statewise data needs reload
  const shouldReloadStatewiseVirtualMachines = useCallback(
    (data: { success: number; warning: number; error: number; total: number }) => {
      return (data?.success === 0 && data?.warning === 0 && data?.error === 0) || data?.total === 0
    },
    [],
  )

  // Helper to get appropriate search action based on user context
  const getSearchAction = useCallback(() => {
    if (allTenants) return searchAllVirtualMachines
    if (!hasCrossTenantPermissions && !allOwners) return searchSelfVirtualMachines
    return searchVirtualMachines
  }, [allTenants, userRole, allOwners])

  // Helper to get appropriate nextPage action based on user context
  const getNextPageAction = useCallback(() => {
    if (allTenants) return listAllVirtualMachinesNextPage
    if (!hasCrossTenantPermissions && !allOwners) return listSelfVirtualMachinesNextPage
    return listVirtualMachinesNextPage
  }, [allTenants, userRole, allOwners])

  // Helper to get appropriate reload actions based on user context
  const getReloadActions = useCallback(() => {
    if (allTenants) {
      return {
        stateCount: allVmStateCount,
        reloadStatewise: allStatewiseReload,
      }
    }
    if (!hasCrossTenantPermissions && !allOwners) {
      return {
        stateCount: selfVmStateCount,
        reloadStatewise: selfStatewiseReload,
      }
    }
    return {
      stateCount: vmStateCount,
      reloadStatewise: statewiseReload,
    }
  }, [
    allTenants,
    userRole,
    allOwners,
    allVmStateCount,
    allStatewiseReload,
    selfVmStateCount,
    selfStatewiseReload,
    vmStateCount,
    statewiseReload,
  ])

  // Common loading function used by both initial load and toggle effects
  const loadVMsData = useCallback(
    async ({ shouldReloadVmCounts = false } = {}) => {
      setIsPaginationLoading(true)
      try {
        const params = buildParams({ searchParams })
        const searchAction = getSearchAction()
        const { stateCount, reloadStatewise } = getReloadActions()

        const promises = []
        // Execute search
        promises.push(searchAction.call(params))

        // Conditionally reload statewise data if all counts are zero.
        // UVMP-FR-001: skip on the host page. The statewise count is one shared slot read by the
        // global VM list, so a host-filtered fetch would make that list show the host's count.
        // The host page gets its own count from the search request instead (UVMP-FR-003).
        if (!host && (shouldReloadStatewiseVirtualMachines(stateCount) || shouldReloadVmCounts)) {
          promises.push(reloadStatewise(true))
        }

        // Wait for all promises to resolve
        await Promise.all(promises)
      } finally {
        setIsPaginationLoading(false)
      }
    },
    [
      host,
      buildParams,
      searchParams,
      getSearchAction,
      getReloadActions,
      shouldReloadStatewiseVirtualMachines,
    ],
  )

  // ========== Computed Values ==========
  const filteredData = useMemo(
    () => selectByContext(allData, selfData, data),
    [selectByContext, allData, selfData, data],
  )

  // Total count of VMs
  const totalCount = useMemo(() => {
    // If search is active, use totalCount from session pagination (search results count)
    if (isSearchActive) {
      const paginationData = selectByContext(
        session?.allVmsPagination,
        session?.selfVmsPagination,
        session?.vmsPagination,
      )
      if (paginationData?.totalCount != null) {
        return paginationData.totalCount
      }
    }

    // Otherwise use lite API data. UVMP-FR-002: only the global list fills this, so it is global.
    const stateWiseCount: StatewiseVirtualMachineCount = selectByContext(
      allVmStateCount,
      selfVmStateCount,
      vmStateCount,
    )
    return stateWiseCount?.total
  }, [selectByContext, isSearchActive, session, allVmStateCount, selfVmStateCount, vmStateCount])

  // Next page availability
  const hasNextPage = useMemo(() => {
    // Check if we have a marker for the next page
    const nextPageMarker = pageMarkers[currentPage + 1]
    if (nextPageMarker) {
      return true
    }

    // Check if based on total count we should have more pages
    if (totalCount) {
      const estimatedMaxPage = Math.ceil(totalCount / pageSize) - 1
      return currentPage < estimatedMaxPage
    }

    return false
  }, [currentPage, pageMarkers, totalCount, pageSize])

  // Paginated data
  const paginatedVMs = useMemo(() => {
    if (!filteredData || !Array.isArray(filteredData)) {
      return []
    }
    const startIndex = currentPage * pageSize
    const endIndex = startIndex + pageSize
    return filteredData.slice(startIndex, endIndex)
  }, [filteredData, currentPage, pageSize])

  // Determine if any VM is in 'deleting' or 'deleted' state to decide whether to trigger immediate refresh on polling
  // For VMs in deleting/deleted state, we want to trigger an immediate refresh on the next poll to update the list quickly,
  const isAnyVMInDeletingState = useMemo(
    () => paginatedVMs.some((vm) => ['deleting', 'deleted'].includes(vm.status)),
    [paginatedVMs],
  )

  // Loading state
  const activeLoading = useMemo(() => {
    // Show loading during pagination
    if (isPaginationLoading) return true
    return selectByContext(allLoading, selfLoading, loading)
  }, [isPaginationLoading, selectByContext, allLoading, selfLoading, loading])

  // Polling state
  const activePolling = useMemo(
    () => selectByContext(pollingAllVms, pollingSelfVms, pollingVms),
    [selectByContext, pollingAllVms, pollingSelfVms, pollingVms],
  )

  // Message state
  const activeMessage = useMemo(
    () => selectByContext(allMessage, selfMessage, message),
    [selectByContext, allMessage, selfMessage, message],
  )

  // Refreshing state
  const activeRefreshing = useMemo(
    () => selectByContext(allVmRefreshing, selfVmRefreshing, vmRefreshing),
    [selectByContext, allVmRefreshing, selfVmRefreshing, vmRefreshing],
  )

  // ========== Callbacks ==========
  const handlePageChange = useCallback(
    async (newPage: number, marker?: string) => {
      // Check if going backward or if we already have data for this page
      const isGoingBackward = newPage < currentPage
      const alreadyHasMarker = pageMarkers[newPage] !== undefined

      // Check if cache has enough data for the requested page
      const currentCachedData = selectByContext(allData, selfData, data)
      const cacheLength = currentCachedData?.length || 0

      // Calculate what slice we'd get for this page
      const startIndex = newPage * pageSize
      const endIndex = startIndex + pageSize

      // Skip API call if cache has data or going backward
      const cacheHasEnoughData = cacheLength >= endIndex
      const cacheHasAllData = totalCount != null && cacheLength >= totalCount

      // Update page immediately for instant navigation
      setCurrentPage(newPage)

      if (
        isGoingBackward ||
        (alreadyHasMarker && newPage > 0) ||
        cacheHasEnoughData ||
        cacheHasAllData
      ) {
        return
      }

      // Only show loading for forward navigation when fetching new data
      setIsPaginationLoading(true)

      try {
        // Determine which marker to use
        let requestMarker: string | undefined

        if (newPage > 0 && newPage > currentPage) {
          requestMarker = marker || pageMarkers[newPage] || undefined
          if (marker && !pageMarkers[newPage]) {
            setPageMarkers((prev) => {
              const updated = [...prev]
              updated[newPage] = marker
              return updated
            })
          }
        } else {
          requestMarker = undefined
        }

        const params = buildParams({
          marker: requestMarker,
          ...(searchParams && { searchParams }),
        })

        if (isSearchActive && searchParams) {
          const searchAction = getSearchAction()
          await searchAction.call(params)
        } else {
          const nextPageAction = getNextPageAction()
          await nextPageAction.call(params)
        }

        setIsPaginationLoading(false)
      } catch (error) {
        setIsPaginationLoading(false)
      }
    },
    [
      currentPage,
      pageMarkers,
      selectByContext,
      allData,
      selfData,
      data,
      pageSize,
      totalCount,
      buildParams,
      searchParams,
      isSearchActive,
      getSearchAction,
      getNextPageAction,
    ],
  )

  const handleRefresh = useCallback(
    async (isLoading: boolean = true) => {
      // Reset pagination
      setCurrentPage(0)
      setPageMarkers([null])
      setIsPaginationLoading(isLoading)

      try {
        const { reloadStatewise } = getReloadActions()

        // Always use explicit search/nextPage actions with params to preserve search state
        const params = buildParams({
          marker: null,
          ...(searchParams && { searchParams }),
        })

        const promises = []
        // Reload statewise and lite data to ensure counts are up to date.
        // UVMP-FR-001: not on the host page, so the shared global count stays global.
        if (!host) {
          promises.push(reloadStatewise(true))
        }

        if (isSearchActive && searchParams) {
          const searchAction = getSearchAction()
          promises.push(searchAction.call(params))
        } else {
          // Use nextPage action even for refresh to include sort/pagination params
          const nextPageAction = getNextPageAction()
          promises.push(nextPageAction.call(params))
        }

        await Promise.all(promises)
      } finally {
        setIsPaginationLoading(false)
      }
    },
    [
      host,
      isSearchActive,
      searchParams,
      buildParams,
      getSearchAction,
      getNextPageAction,
      getReloadActions,
    ],
  )

  const activePoll = useMemo(() => {
    // Build params for polling with current page context
    const pollParams = buildParams({
      marker: pageMarkers[currentPage],
      ...(searchParams && { searchParams }),
    })

    const pollAction = selectByContext(pollAllVms, pollSelfVms, pollVms)
    return () => pollAction(pollParams)
  }, [
    buildParams,
    pageMarkers,
    currentPage,
    searchParams,
    selectByContext,
    pollAllVms,
    pollSelfVms,
    pollVms,
  ])

  // Helper to get the appropriate dataKey based on user context
  const getDataKey = useCallback(
    () =>
      selectByContext(
        DataKeys.AllOpenstackVirtualMachines,
        DataKeys.SelfOpenstackVirtualMachines,
        DataKeys.OpenstackVirtualMachines,
      ),
    [selectByContext],
  )

  // Helper to update cached params in session after fetching data
  const updateCachedParamsInSession = useCallback(
    (params: { searchParams: any; sortKey: string; sortDir: string; pageSize: number }) => {
      const dataKey = getDataKey()
      const sessionKey =
        dataKey === DataKeys.AllOpenstackVirtualMachines
          ? sessionAllVMCachedParams
          : dataKey === DataKeys.SelfOpenstackVirtualMachines
            ? sessionSelfVMCachedParams
            : sessionVMCachedParams
      dispatch(sessionActions.updateSession({ [sessionKey]: params }))
    },
    [
      getDataKey,
      sessionAllVMCachedParams,
      sessionSelfVMCachedParams,
      sessionVMCachedParams,
      dispatch,
    ],
  )

  // Handle search - use lightweight search actions (no dependencies)
  const handleSearch = useCallback(
    async (field: string, value: string, filterParams: Record<string, string> = {}) => {
      // Clear grid selection — filtered-out rows would stay stuck otherwise
      clearSelection()
      // Combine search field + value with filter params
      const newSearchParams = value ? { [field]: value, ...filterParams } : { ...filterParams }
      setSearchParams(newSearchParams)
      setIsSearchActive(true)
      setCurrentPage(0)
      setPageMarkers([null])
      setIsPaginationLoading(true)

      // Save search params to session
      dispatch(sessionActions.updateSession({ [sessionSearchParams]: newSearchParams }))

      try {
        const params = buildParams({ searchParams: newSearchParams })
        updateCachedParamsInSession({ searchParams: newSearchParams, sortKey, sortDir, pageSize })

        const searchAction = getSearchAction()
        await searchAction.call(params)
      } finally {
        setIsPaginationLoading(false)
      }
    },
    [
      clearSelection,
      dispatch,
      sessionSearchParams,
      buildParams,
      updateCachedParamsInSession,
      sortKey,
      sortDir,
      pageSize,
      getSearchAction,
    ],
  )

  // Handle clear search - use lightweight actions to refetch without dependencies
  const handleClearSearch = useCallback(async () => {
    // Clear grid selection
    clearSelection()
    setSearchParams(host ? { host } : null)
    setIsSearchActive(!!host)
    setCurrentPage(0)
    setPageMarkers([null])
    setIsPaginationLoading(true)

    // Clear search-specific pagination data from session
    const cachedParamsKey = selectByContext(
      sessionAllVMCachedParams,
      sessionSelfVMCachedParams,
      sessionVMCachedParams,
    )
    dispatch(
      sessionActions.updateSession({
        [cachedParamsKey]: undefined,
        [sessionSearchParams]: null,
      }),
    )

    try {
      const params = buildParams({})
      updateCachedParamsInSession({ searchParams: null, sortKey, sortDir, pageSize })

      if (isSearchActive && searchParams) {
        const searchAction = getSearchAction()
        await searchAction.call(params)
      } else {
        // Use nextPage action even for refresh to include sort/pagination params
        const nextPageAction = getNextPageAction()
        await nextPageAction.call(params)
      }
    } finally {
      setIsPaginationLoading(false)
    }
  }, [
    clearSelection,
    selectByContext,
    sessionAllVMCachedParams,
    sessionSelfVMCachedParams,
    sessionVMCachedParams,
    sessionSearchParams,
    dispatch,
    buildParams,
    updateCachedParamsInSession,
    sortKey,
    sortDir,
    pageSize,
    isSearchActive,
    searchParams,
    getSearchAction,
    getNextPageAction,
  ])

  // Convert API format searchParams to VMFilterParams format for UI
  // API format: user_id -> VMFilterParams format: owner
  const filterParamsForUI = useMemo((): VMFilterParams => {
    if (!searchParams) {
      return defaultSearchParams
    }
    return {
      cluster: searchParams.cluster || 'all',
      vm_state: searchParams.vm_state || 'all',
      owner: searchParams.owner || 'all',
      flavor: searchParams.flavor || 'all',
      image: searchParams.image || 'all',
      tenant_id: searchParams.tenant_id || '',
    }
  }, [searchParams])

  // Handle sort - reload data with new sort parameters
  const handleSort = useCallback(
    async (newSortKey: string, newSortDir: 'asc' | 'desc') => {
      // Clear grid selection — sorting re-paginates server-side
      clearSelection()
      setSortKey(newSortKey)
      setSortDir(newSortDir)
      setCurrentPage(0)
      setPageMarkers([null])
      setIsPaginationLoading(true)

      // Save sort key and direction to session using dynamic keys
      dispatch(
        sessionActions.updateSession({
          [sessionSortKey]: newSortKey,
          [sessionSortDir]: newSortDir,
        }),
      )

      // Read current session to get latest search params
      const currentSession = store.getState().session
      const currentSearchParams = currentSession?.[sessionSearchParams]

      try {
        const params = buildParams({
          sortKey: newSortKey,
          sortDir: newSortDir,
          ...(currentSearchParams && { searchParams: currentSearchParams }),
        })

        // Use search or nextPage action based on whether search is active
        const action = currentSearchParams ? getSearchAction() : getNextPageAction()
        await action.call(params)
      } finally {
        setIsPaginationLoading(false)
      }
    },
    [
      clearSelection,
      dispatch,
      sessionSortKey,
      sessionSortDir,
      sessionSearchParams,
      buildParams,
      getSearchAction,
      getNextPageAction,
    ],
  )

  // Handle page size change - reload data with new page size
  const handlePageSizeChange = useCallback(
    async (newPageSize: number) => {
      setPageSize(newPageSize)
      setCurrentPage(0)
      setPageMarkers([null])

      // Persist page size preference to session
      dispatch(sessionActions.updateSession({ [sessionPageSize]: newPageSize }))

      // Check if cache already has enough data for the new page size
      const currentCachedData = selectByContext(allData, selfData, data)
      const cacheLength = currentCachedData?.length || 0
      const cacheHasEnoughData = cacheLength >= newPageSize
      const cacheHasAllData = totalCount != null && cacheLength >= totalCount

      // Skip API call if cache has sufficient data
      if (cacheHasEnoughData || cacheHasAllData) {
        return
      }

      setIsPaginationLoading(true)

      try {
        const params = buildParams({
          limit: newPageSize,
          ...(searchParams && { searchParams }),
        })
        const action = isSearchActive && searchParams ? getSearchAction() : getNextPageAction()
        await action.call(params)
      } finally {
        setIsPaginationLoading(false)
      }
    },
    [
      dispatch,
      sessionPageSize,
      selectByContext,
      allData,
      selfData,
      data,
      totalCount,
      buildParams,
      isSearchActive,
      searchParams,
      getSearchAction,
      getNextPageAction,
    ],
  )

  // ========== Effects ==========
  // Load data when toggling between allTenants/allOwners
  useEffect(() => {
    // Skip on initial mount
    if (isInitialMount.current) {
      isInitialMount.current = false
      return
    }

    // Reset pagination when context changes
    setCurrentPage(0)
    setPageMarkers([null])
    loadVMsData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allTenants, allOwners])

  // Initial data load
  useEffect(() => {
    if (hasInitialDataLoaded.current) {
      return
    }
    hasInitialDataLoaded.current = true

    // Check if data exists for current view
    const currentData = selectByContext(allData, selfData, data)
    const hasData = currentData && currentData.length > 0

    // Check if we should reload based on last visited page
    const isFirstVisit = session?.lastVisitedVmPage == null
    const needsReload =
      sessionKey === 'vmList'
        ? session?.lastVisitedVmPage === 'hostList'
        : session?.lastVisitedVmPage === 'vmList'

    const hostChanged =
      session?.lastVisitedVmPage === 'hostList' && host !== cachedSearchParams?.host

    const shouldReload = !hasData || isFirstVisit || needsReload || hostChanged || hasUrlFilters

    // Update session to mark this page as last visited
    dispatch(sessionActions.updateSession({ lastVisitedVmPage: sessionKey }))

    if (!shouldReload) {
      return
    }

    // Update search params in session when host changed or URL filters override session
    if (hostChanged || hasUrlFilters) {
      dispatch(
        sessionActions.updateSession({
          [sessionSearchParams]: searchParams,
        }),
      )
    }

    loadVMsData({ shouldReloadVmCounts: hasUrlFilters })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ========== Loading States ==========
  const isStatewiseVMsLoading = useMemo(
    () => selectByContext(statewiseLoading, allStatewiseLoading, selfStatewiseLoading),
    [selectByContext, statewiseLoading, allStatewiseLoading, selfStatewiseLoading],
  )

  // Return all the data and functions that components need
  return {
    // Data
    data: filteredData,
    paginatedVMs,
    totalCount,

    // Loading states
    loading: activeLoading,
    polling: activePolling,
    refreshing: activeRefreshing,
    message: activeMessage,

    // Pagination
    currentPage,
    pageSize,
    pageMarkers,
    hasNextPage,
    isPaginationLoading,
    handlePageSizeChange,

    // Actions
    reload: handleRefresh,
    poll: activePoll,
    handlePageChange,
    handleRefresh,
    setCurrentPage,
    setPageSize,
    setPageMarkers,
    setSearchParams,
    setIsSearchActive,

    // Search
    searchParams,
    isSearchActive,
    handleSearch,
    handleClearSearch,
    filterParamsForUI,

    // Selection management
    clearSelectionRef,
    clearSelection,

    // Sort
    sortKey,
    sortDir,
    handleSort,

    // Statewise counts
    vmStateCount: selectByContext(allVmStateCount, selfVmStateCount, vmStateCount),
    isStatewiseVMsLoading,
  }
}
